import { useState, useEffect, useCallback, useRef } from 'react'
import {
  collection, onSnapshot, addDoc, deleteDoc, doc, query, updateDoc,
  where, orderBy, serverTimestamp, getDocs, setDoc,
} from 'firebase/firestore'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  ComposedChart, Area, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts'
import {
  Wallet, TrendingUp, TrendingDown, DollarSign, Trash2,
  RefreshCw, ChevronLeft, ChevronRight, AlertCircle,
  FileDown, Paperclip, Image, X, Camera, Upload, Scale,
  Settings, Pencil, ArrowUpCircle, ArrowDownCircle, Save, Check,
} from 'lucide-react'
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns'
import { es } from 'date-fns/locale'
import { db } from '@/config/firebase'
import { useAuth } from '@/hooks/useAuth'
import { useCompany } from '@/hooks/useCompany'
import { uploadFinanceReceipt, deleteStorageFile, type UploadedFile } from '@/services/storage.service'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { FunctionsService } from '@/services/functions.service'

// ── Types ──────────────────────────────────────────────────────────────────────
type MovementKind = 'income' | 'expense'

interface Category {
  id: string
  name: string
  color: string
  type: MovementKind
}

interface Movement {
  id: string
  amount: number
  category: string
  description: string
  date: string
  createdBy: string
  receipts?: UploadedFile[]
  kind: MovementKind
}

// Categorías por defecto — se siembran en Firestore la primera vez (IDs estables
// para no romper egresos ya guardados con estas claves).
const DEFAULT_CATEGORIES: Category[] = [
  { id: 'nomina',           name: 'Nómina',           color: '#6366f1', type: 'expense' },
  { id: 'renta',            name: 'Renta',            color: '#f59e0b', type: 'expense' },
  { id: 'inventario',       name: 'Inventario',       color: '#10b981', type: 'expense' },
  { id: 'marketing',        name: 'Marketing',        color: '#ec4899', type: 'expense' },
  { id: 'utilidades',       name: 'Utilidades',       color: '#8b5cf6', type: 'expense' },
  { id: 'operaciones',      name: 'Operaciones',      color: '#14b8a6', type: 'expense' },
  { id: 'otros',            name: 'Otros',            color: '#64748b', type: 'expense' },
  { id: 'ingreso_efectivo', name: 'Efectivo / ATH',   color: '#0ea5e9', type: 'income'  },
  { id: 'ingreso_otro',     name: 'Otros ingresos',   color: '#22c55e', type: 'income'  },
]

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

const fmtShort = (n: number) =>
  Math.abs(n) >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`

const collForKind = (kind: MovementKind) => (kind === 'income' ? 'incomes' : 'expenses')

// ── Custom tooltip ─────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-gray-900 border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/50 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }} className="font-semibold">
          {p.name}: {fmt(p.value)}
        </p>
      ))}
    </div>
  )
}

// ── Category manager modal ───────────────────────────────────────────────────────
function CategoryManager({
  open, onClose, categories, onToast,
}: {
  open: boolean
  onClose: () => void
  categories: Category[]
  onToast: (t: { title: string; description?: string; variant?: 'destructive' }) => void
}) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<{ name: string; color: string; type: MovementKind }>({
    name: '', color: '#6366f1', type: 'expense',
  })

  const resetForm = () => { setEditingId(null); setForm({ name: '', color: '#6366f1', type: 'expense' }) }

  const startEdit = (c: Category) => {
    setEditingId(c.id)
    setForm({ name: c.name, color: c.color, type: c.type })
  }

  const handleSaveCat = async () => {
    if (!form.name.trim()) { onToast({ title: 'Ponle un nombre a la categoría', variant: 'destructive' }); return }
    setSaving(true)
    try {
      if (editingId) {
        await updateDoc(doc(db, 'financeCategories', editingId), {
          name: form.name.trim(), color: form.color, type: form.type,
        })
        onToast({ title: 'Categoría actualizada' })
      } else {
        await addDoc(collection(db, 'financeCategories'), {
          name: form.name.trim(), color: form.color, type: form.type,
        })
        onToast({ title: 'Categoría creada' })
      }
      resetForm()
    } catch (err: any) {
      onToast({ title: 'Error', description: err.message, variant: 'destructive' })
    }
    setSaving(false)
  }

  const handleDeleteCat = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'financeCategories', id))
      onToast({ title: 'Categoría eliminada' })
      setConfirmDeleteId(null)
      if (editingId === id) resetForm()
    } catch (err: any) {
      onToast({ title: 'Error', description: err.message, variant: 'destructive' })
    }
  }

  const groups: { label: string; type: MovementKind }[] = [
    { label: 'Ingresos', type: 'income' },
    { label: 'Egresos', type: 'expense' },
  ]

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { resetForm(); onClose() } }}>
      <DialogContent className="bg-gray-900 border-white/10 text-white sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="h-4 w-4" /> Categorías</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {groups.map(g => {
            const list = categories.filter(c => c.type === g.type).sort((a, b) => a.name.localeCompare(b.name))
            return (
              <div key={g.type}>
                <p className="text-xs uppercase tracking-wide text-white/40 mb-1.5">{g.label}</p>
                <div className="space-y-1">
                  {list.length === 0 && <p className="text-xs text-white/25 py-1">Sin categorías de {g.label.toLowerCase()}</p>}
                  {list.map(c => (
                    <div key={c.id} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
                      <span className="h-3.5 w-3.5 rounded-full shrink-0" style={{ background: c.color }} />
                      <span className="text-sm text-white/80 flex-1 truncate">{c.name}</span>
                      {confirmDeleteId === c.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-red-300 mr-1">¿Eliminar?</span>
                          <button onClick={() => handleDeleteCat(c.id)} className="p-1 rounded text-red-400 hover:bg-red-500/10"><Check className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setConfirmDeleteId(null)} className="p-1 rounded text-white/40 hover:bg-white/10"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEdit(c)} className="p-1 rounded text-blue-400/70 hover:text-blue-400 hover:bg-blue-500/10"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setConfirmDeleteId(c.id)} className="p-1 rounded text-white/30 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          {/* Add / edit form */}
          <div className="rounded-xl border border-white/10 bg-white/3 p-3 space-y-3">
            <p className="text-xs font-semibold text-white/60">{editingId ? 'Editar categoría' : 'Nueva categoría'}</p>
            <div className="flex gap-2">
              <input
                type="color"
                value={form.color}
                onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                className="h-9 w-10 rounded cursor-pointer bg-transparent border border-white/10 shrink-0"
                title="Color"
              />
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Nombre (ej. Servicios, Comisiones…)"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25"
              />
              <Select value={form.type} onValueChange={(v: MovementKind) => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/10 text-white">
                  <SelectItem value="income">Ingreso</SelectItem>
                  <SelectItem value="expense">Egreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              {editingId && (
                <Button variant="outline" size="sm" onClick={resetForm} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                  Cancelar
                </Button>
              )}
              <Button size="sm" onClick={handleSaveCat} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                {editingId ? 'Guardar' : 'Agregar'}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onClose() }} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function FinanzasPage() {
  const { user } = useAuth()
  const company = useCompany()
  const { toast, toasts, dismiss } = useToast()

  // Selected month
  const [month, setMonth] = useState(startOfMonth(new Date()))
  const monthLabel = format(month, 'MMMM yyyy', { locale: es })

  // Categories (Firestore, dynamic)
  const [categories, setCategories] = useState<Category[]>([])
  const seededRef = useRef(false)
  const [catManagerOpen, setCatManagerOpen] = useState(false)

  // Shopify income (Cloud Function)
  const [income, setIncome] = useState<number | null>(null)
  const [loadingIncome, setLoadingIncome] = useState(false)
  const [incomeError, setIncomeError] = useState<string | null>(null)

  // Movements (real-time from Firestore)
  const [expenses, setExpenses] = useState<Movement[]>([])
  const [incomes, setIncomes] = useState<Movement[]>([])
  const [loadingList, setLoadingList] = useState(true)

  // Add / edit dialog
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Movement | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<{ amount: string; category: string; description: string; date: string; kind: MovementKind }>({
    amount: '', category: '', description: '', date: format(new Date(), 'yyyy-MM-dd'), kind: 'expense',
  })
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [existingReceipts, setExistingReceipts] = useState<UploadedFile[]>([])
  const [removedReceipts, setRemovedReceipts] = useState<UploadedFile[]>([])

  // Receipt viewer
  const [viewingReceipts, setViewingReceipts] = useState<UploadedFile[] | null>(null)

  // Historical chart data (6 months)
  const [chartData, setChartData] = useState<
    { month: string; ingresos: number; egresos: number; ganancia: number }[]
  >([])
  const [loadingChart, setLoadingChart] = useState(true)

  // ── Subscribe categories (+ seed defaults on first empty load) ─────────────────
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'financeCategories'), async snap => {
      if (snap.empty && !seededRef.current) {
        seededRef.current = true
        try {
          await Promise.all(
            DEFAULT_CATEGORIES.map(c =>
              setDoc(doc(db, 'financeCategories', c.id), { name: c.name, color: c.color, type: c.type })
            )
          )
        } catch (err) {
          console.error('No se pudieron sembrar categorías', err)
        }
        return // el snapshot se re-disparará con los datos sembrados
      }
      setCategories(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as Category[])
    })
    return unsub
  }, [])

  const catFor = useCallback(
    (id: string): { name: string; color: string } =>
      categories.find(c => c.id === id) ?? { name: id || 'Sin categoría', color: '#64748b' },
    [categories]
  )
  const categoriesOfKind = useCallback(
    (kind: MovementKind) => categories.filter(c => c.type === kind).sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  )

  // ── Load Shopify income ────────────────────────────────────────────────────────
  const loadIncome = useCallback(async () => {
    setLoadingIncome(true)
    setIncomeError(null)
    const from = startOfMonth(month)
    const to = endOfMonth(month)
    const result = await FunctionsService.getSalesReport(from, to)
    if (result.data) {
      setIncome(result.data.totalNetSales ?? 0)
    } else {
      setIncomeError(result.error ?? 'Error cargando ingresos')
      setIncome(null)
    }
    setLoadingIncome(false)
  }, [month])

  useEffect(() => { loadIncome() }, [loadIncome])

  // ── Subscribe expenses + incomes for the month ─────────────────────────────────
  useEffect(() => {
    setLoadingList(true)
    const from = format(startOfMonth(month), 'yyyy-MM-dd')
    const to = format(endOfMonth(month), 'yyyy-MM-dd')

    const sub = (coll: string, kind: MovementKind, setter: (m: Movement[]) => void) => {
      const q = query(
        collection(db, coll),
        where('date', '>=', from),
        where('date', '<=', to),
        orderBy('date', 'desc')
      )
      return onSnapshot(
        q,
        snap => {
          setter(snap.docs.map(d => ({ id: d.id, ...(d.data() as any), kind })) as Movement[])
          setLoadingList(false)
        },
        () => setLoadingList(false)
      )
    }

    const unsubE = sub('expenses', 'expense', setExpenses)
    const unsubI = sub('incomes', 'income', setIncomes)
    return () => { unsubE(); unsubI() }
  }, [month])

  // ── Build chart data (last 6 months: ingresos reales + manuales, egresos) ──────
  useEffect(() => {
    const fetchChart = async () => {
      setLoadingChart(true)
      const months = Array.from({ length: 6 }, (_, i) => subMonths(startOfMonth(new Date()), 5 - i))
      const rows = await Promise.all(
        months.map(async m => {
          const from = startOfMonth(m)
          const to = endOfMonth(m)
          const fromStr = format(from, 'yyyy-MM-dd')
          const toStr = format(to, 'yyyy-MM-dd')

          const sumAmount = async (coll: string) => {
            try {
              const snap = await getDocs(
                query(collection(db, coll), where('date', '>=', fromStr), where('date', '<=', toStr))
              )
              return snap.docs.reduce((s, d) => s + (d.data().amount ?? 0), 0)
            } catch { return 0 }
          }

          const [egresos, manualIncome] = await Promise.all([sumAmount('expenses'), sumAmount('incomes')])

          let shopifyIncome = 0
          try {
            const res = await FunctionsService.getSalesReport(from, to)
            shopifyIncome = res.data?.totalNetSales ?? 0
          } catch { shopifyIncome = 0 }

          const ingresos = shopifyIncome + manualIncome
          return {
            month: format(m, 'MMM', { locale: es }),
            ingresos,
            egresos,
            ganancia: ingresos - egresos,
          }
        })
      )
      setChartData(rows)
      setLoadingChart(false)
    }
    fetchChart()
  }, [])

  // ── Derived values ───────────────────────────────────────────────────────────
  const totalEgresos = expenses.reduce((s, e) => s + e.amount, 0)
  const totalManualIncome = incomes.reduce((s, e) => s + e.amount, 0)
  const hasIncomeData = income !== null || totalManualIncome > 0
  const totalIngresos = (income ?? 0) + totalManualIncome
  const ganancia = hasIncomeData ? totalIngresos - totalEgresos : null
  const margen = hasIncomeData && totalIngresos > 0 ? ((ganancia ?? 0) / totalIngresos) * 100 : null
  const esRentable = ganancia !== null && ganancia > 0
  const enPerdida = ganancia !== null && ganancia < 0

  // Movimientos combinados (ingresos + egresos) ordenados por fecha
  const movements = [...incomes, ...expenses].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))

  const expensesByCategory = categoriesOfKind('expense')
    .map(c => ({
      name: c.name,
      value: expenses.filter(e => e.category === c.id).reduce((s, e) => s + e.amount, 0),
      color: c.color,
    }))
    .filter(d => d.value > 0)

  // Mantener el mes seleccionado sincronizado en la gráfica de tendencia
  useEffect(() => {
    const label = format(month, 'MMM', { locale: es })
    setChartData(prev =>
      prev.map(d => {
        if (d.month !== label) return d
        const ing = hasIncomeData ? totalIngresos : d.ingresos
        return { ...d, ingresos: ing, egresos: totalEgresos, ganancia: ing - totalEgresos }
      })
    )
  }, [totalIngresos, totalEgresos, hasIncomeData, month])

  // ── Export PDF ───────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const pdf = new jsPDF()
    const periodStr = format(month, 'MMMM yyyy', { locale: es })
    pdf.setFontSize(18); pdf.setTextColor(40, 40, 40); pdf.text(company.name, 14, 18)
    pdf.setFontSize(11); pdf.setTextColor(100, 100, 100)
    pdf.text(`Reporte Financiero — ${periodStr}`, 14, 26)
    pdf.text(`Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 32)
    pdf.setFontSize(12); pdf.setTextColor(40, 40, 40); pdf.text('Resumen', 14, 44)
    autoTable(pdf, {
      startY: 48,
      head: [['Concepto', 'Monto']],
      body: [
        ['Ingresos Shopify', income !== null ? fmt(income) : 'N/A'],
        ['Ingresos manuales', fmt(totalManualIncome)],
        ['Ingresos totales', hasIncomeData ? fmt(totalIngresos) : 'N/A'],
        ['Egresos (Gastos)', fmt(totalEgresos)],
        ['Ganancia Neta', ganancia !== null ? fmt(ganancia) : 'N/A'],
        ['Margen %', margen !== null ? `${margen.toFixed(1)}%` : 'N/A'],
        ['Rentabilidad', ganancia === null ? 'N/A' : ganancia > 0 ? 'Positiva (rentable)' : ganancia < 0 ? 'Negativa (pérdida)' : 'Equilibrio'],
      ],
      theme: 'striped', headStyles: { fillColor: [37, 99, 235] }, margin: { left: 14, right: 14 },
    })
    let y = (pdf as any).lastAutoTable.finalY + 12
    if (incomes.length > 0) {
      pdf.setFontSize(12); pdf.text('Ingresos manuales', 14, y)
      autoTable(pdf, {
        startY: y + 4,
        head: [['Fecha', 'Descripción', 'Categoría', 'Monto']],
        body: incomes.map(e => [e.date, e.description, catFor(e.category).name, fmt(e.amount)]),
        theme: 'striped', headStyles: { fillColor: [16, 185, 129] }, margin: { left: 14, right: 14 },
      })
      y = (pdf as any).lastAutoTable.finalY + 12
    }
    pdf.setFontSize(12); pdf.text('Detalle de Egresos', 14, y)
    autoTable(pdf, {
      startY: y + 4,
      head: [['Fecha', 'Descripción', 'Categoría', 'Monto']],
      body: expenses.map(e => [e.date, e.description, catFor(e.category).name, fmt(e.amount)]),
      theme: 'striped', headStyles: { fillColor: [244, 63, 94] }, margin: { left: 14, right: 14 },
    })
    pdf.save(`finanzas_${format(month, 'yyyy-MM')}.pdf`)
  }

  // ── Export CSV ───────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const rows = [
      ['Tipo', 'Fecha', 'Descripción', 'Categoría', 'Monto', 'Registrado por'],
      ...movements.map(e => [
        e.kind === 'income' ? 'Ingreso' : 'Egreso',
        e.date,
        `"${e.description.replace(/"/g, '""')}"`,
        catFor(e.category).name,
        (e.kind === 'income' ? e.amount : -e.amount).toFixed(2),
        e.createdBy,
      ]),
    ]
    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `movimientos_${format(month, 'yyyy-MM')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Dialog helpers ─────────────────────────────────────────────────────────────
  const openAdd = (kind: MovementKind) => {
    setEditing(null)
    const firstCat = categoriesOfKind(kind)[0]?.id ?? ''
    setForm({ amount: '', category: firstCat, description: '', date: format(new Date(), 'yyyy-MM-dd'), kind })
    setPendingFiles([]); setExistingReceipts([]); setRemovedReceipts([])
    setDialogOpen(true)
  }

  const openEdit = (m: Movement) => {
    setEditing(m)
    setForm({ amount: String(m.amount), category: m.category, description: m.description, date: m.date, kind: m.kind })
    setPendingFiles([]); setExistingReceipts(m.receipts ?? []); setRemovedReceipts([])
    setDialogOpen(true)
  }

  const setKind = (kind: MovementKind) => {
    setForm(f => ({ ...f, kind, category: categoriesOfKind(kind)[0]?.id ?? '' }))
  }

  const closeDialog = () => {
    setDialogOpen(false); setEditing(null); setPendingFiles([]); setExistingReceipts([]); setRemovedReceipts([])
  }

  // ── Save (add or edit) ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    const amount = parseFloat(form.amount)
    if (!amount || amount <= 0) { toast({ title: 'Monto inválido', variant: 'destructive' }); return }
    if (!form.description.trim()) { toast({ title: 'Agrega una descripción', variant: 'destructive' }); return }
    if (!form.category) { toast({ title: 'Selecciona una categoría', description: 'Crea una con el engranaje si no hay.', variant: 'destructive' }); return }
    setSaving(true)
    try {
      if (editing) {
        const refDoc = doc(db, collForKind(editing.kind), editing.id)
        // Borrar de Storage los soportes que se quitaron
        if (removedReceipts.length) {
          await Promise.all(removedReceipts.map(r => deleteStorageFile(r.path)))
        }
        let receipts = existingReceipts
        if (pendingFiles.length > 0) {
          const uploaded = await Promise.all(
            pendingFiles.map((f, i) => uploadFinanceReceipt(editing.kind, editing.id, f, existingReceipts.length + i))
          )
          receipts = [...existingReceipts, ...uploaded]
        }
        await updateDoc(refDoc, {
          amount, category: form.category, description: form.description.trim(), date: form.date, receipts,
        })
        toast({ title: editing.kind === 'income' ? 'Ingreso actualizado' : 'Egreso actualizado' })
      } else {
        const coll = collForKind(form.kind)
        const docRef = await addDoc(collection(db, coll), {
          amount, category: form.category, description: form.description.trim(), date: form.date,
          createdAt: serverTimestamp(), createdBy: user?.email ?? user?.uid ?? 'unknown', receipts: [],
        })
        if (pendingFiles.length > 0) {
          const uploaded = await Promise.all(
            pendingFiles.map((f, i) => uploadFinanceReceipt(form.kind, docRef.id, f, i))
          )
          await updateDoc(docRef, { receipts: uploaded })
        }
        toast({
          title: form.kind === 'income' ? 'Ingreso registrado' : 'Egreso registrado',
          description: pendingFiles.length > 0 ? `${pendingFiles.length} soporte(s) adjuntado(s)` : undefined,
        })
      }
      closeDialog()
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    }
    setSaving(false)
  }

  const handleDelete = async (m: Movement) => {
    try {
      if (m.receipts?.length) await Promise.all(m.receipts.map(r => deleteStorageFile(r.path)))
      await deleteDoc(doc(db, collForKind(m.kind), m.id))
      toast({ title: m.kind === 'income' ? 'Ingreso eliminado' : 'Egreso eliminado' })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    }
  }

  // ── Summary card ─────────────────────────────────────────────────────────────
  function SummaryCard({
    title, value, icon: Icon, color, loading, prefix = '$', suffix = '', note, noteTone = 'muted',
  }: {
    title: string; value: number | null; icon: any; color: string
    loading?: boolean; prefix?: string; suffix?: string; note?: string
    noteTone?: 'good' | 'bad' | 'muted'
  }) {
    const noteClass = noteTone === 'good' ? 'text-emerald-400' : noteTone === 'bad' ? 'text-red-400' : 'text-white/30'
    const isNegative = value !== null && value < 0
    return (
      <div className="rounded-xl border border-white/8 bg-white/3 p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-white/50 text-sm">{title}</p>
          <div className="p-2 rounded-lg" style={{ background: color + '20' }}>
            <Icon className="h-4 w-4" style={{ color }} />
          </div>
        </div>
        {loading ? (
          <div className="h-8 w-32 bg-white/10 rounded animate-pulse" />
        ) : value !== null ? (
          <p className="text-2xl font-bold font-mono" style={{ color: isNegative ? '#f43f5e' : '#ffffff' }}>
            {isNegative ? '−' : ''}{prefix}{Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{suffix}
          </p>
        ) : (
          <p className="text-white/30 text-lg">—</p>
        )}
        {note && <p className={`text-xs mt-1 ${noteClass}`}>{note}</p>}
      </div>
    )
  }

  const dialogCats = categoriesOfKind(form.kind)

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map(t => (
          <div key={t.id} onClick={() => dismiss(t.id!)}
            className={`cursor-pointer rounded-lg px-4 py-3 shadow-lg text-sm max-w-xs ${
              t.variant === 'destructive' ? 'bg-red-900/90 text-red-100' : 'bg-gray-800 text-white'}`}>
            <p className="font-semibold">{t.title}</p>
            {t.description && <p className="text-white/70 mt-0.5">{t.description}</p>}
          </div>
        ))}
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        <Header />

        {/* ── Page header ── */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 mt-4 md:mt-0">
          <div className="flex items-center gap-3">
            <Wallet className="h-6 w-6 text-emerald-400" />
            <div>
              <h1 className="text-xl font-bold text-white">Finanzas</h1>
              <p className="text-white/40 text-sm">Ingresos & Egresos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCatManagerOpen(true)}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              <Settings className="h-3.5 w-3.5 mr-1.5" />Categorías
            </Button>
            <Button variant="outline" size="icon" onClick={() => setMonth(m => subMonths(m, 1))}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 h-8 w-8">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-white font-medium text-sm min-w-[140px] text-center capitalize">{monthLabel}</span>
            <Button variant="outline" size="icon" onClick={() => setMonth(m => addMonths(m, 1))}
              disabled={month >= startOfMonth(new Date())}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 h-8 w-8 disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={loadIncome} disabled={loadingIncome}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 ml-2">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loadingIncome ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* ── Income error ── */}
        {incomeError && (
          <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/25 rounded-lg px-4 py-3 mb-4 text-sm text-amber-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {incomeError} — configura Shopify en /config para ver ingresos de ventas
          </div>
        )}

        {/* ── Summary cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <SummaryCard
            title="Ingresos"
            value={hasIncomeData ? totalIngresos : null}
            icon={TrendingUp}
            color="#10b981"
            loading={loadingIncome}
            note={totalManualIncome > 0 ? `incluye ${fmt(totalManualIncome)} manuales` : undefined}
          />
          <SummaryCard title="Egresos" value={totalEgresos} icon={TrendingDown} color="#f43f5e" />
          <SummaryCard
            title="Ganancia Neta"
            value={ganancia}
            icon={DollarSign}
            color={esRentable ? '#10b981' : enPerdida ? '#f43f5e' : '#6366f1'}
            loading={loadingIncome}
            note={esRentable ? '✓ Negocio rentable' : enPerdida ? '⚠ Pérdida neta' : undefined}
            noteTone={esRentable ? 'good' : enPerdida ? 'bad' : 'muted'}
          />
          <SummaryCard
            title="Margen"
            value={margen}
            icon={Wallet}
            color={esRentable ? '#10b981' : enPerdida ? '#f43f5e' : '#f59e0b'}
            loading={loadingIncome}
            prefix=""
            suffix="%"
            note={margen !== null ? (margen >= 0 ? 'Rentabilidad sobre ventas' : 'Margen negativo') : undefined}
            noteTone={margen !== null ? (margen >= 0 ? 'good' : 'bad') : 'muted'}
          />
        </div>

        {/* ── Estado de rentabilidad del negocio ── */}
        {ganancia !== null && (
          <div className={`flex items-center gap-3 rounded-xl border px-5 py-3 mb-6 ${
            esRentable ? 'border-emerald-500/25 bg-emerald-500/10'
              : enPerdida ? 'border-red-500/25 bg-red-500/10'
              : 'border-white/10 bg-white/5'}`}>
            <div className={`p-2 rounded-lg ${esRentable ? 'bg-emerald-500/20' : enPerdida ? 'bg-red-500/20' : 'bg-white/10'}`}>
              <Scale className={`h-4 w-4 ${esRentable ? 'text-emerald-400' : enPerdida ? 'text-red-400' : 'text-white/50'}`} />
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${esRentable ? 'text-emerald-300' : enPerdida ? 'text-red-300' : 'text-white/70'}`}>
                {esRentable ? 'El negocio está en positivo (rentable)' : enPerdida ? 'El negocio está en negativo (pérdida)' : 'Punto de equilibrio'}
              </p>
              <p className="text-xs text-white/45 mt-0.5">
                {monthLabel}: ingresos {hasIncomeData ? fmt(totalIngresos) : '—'} − egresos {fmt(totalEgresos)} ={' '}
                <span className={esRentable ? 'text-emerald-400' : enPerdida ? 'text-red-400' : 'text-white/60'}>
                  {ganancia < 0 ? '−' : ''}{fmt(Math.abs(ganancia))}
                </span>
                {margen !== null && <> · margen {margen.toFixed(1)}%</>}
              </p>
            </div>
          </div>
        )}

        {/* ── Charts row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* 6-month trend */}
          <div className="lg:col-span-2 rounded-xl border border-white/8 bg-white/3 p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold text-white/70">Tendencia 6 meses — ventas, gastos y ganancia</p>
              {loadingChart && <RefreshCw className="h-3.5 w-3.5 text-white/30 animate-spin" />}
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="gIngresos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gEgresos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="month" tick={{ fill: '#ffffff50', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmtShort} tick={{ fill: '#ffffff50', fontSize: 10 }} axisLine={false} tickLine={false} width={52} />
                <Tooltip content={<ChartTooltip />} />
                <Legend formatter={v => <span className="text-white/60 text-xs">{v}</span>} />
                <ReferenceLine y={0} stroke="#ffffff30" strokeDasharray="4 4" />
                <Area type="monotone" dataKey="ingresos" name="Ingresos" stroke="#10b981" strokeWidth={2} fill="url(#gIngresos)" />
                <Area type="monotone" dataKey="egresos" name="Egresos" stroke="#f43f5e" strokeWidth={2} fill="url(#gEgresos)" />
                <Line type="monotone" dataKey="ganancia" name="Ganancia" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3, fill: '#6366f1' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Expense breakdown pie */}
          <div className="rounded-xl border border-white/8 bg-white/3 p-5">
            <p className="text-sm font-semibold text-white/70 mb-4">Egresos por categoría</p>
            {expensesByCategory.length === 0 ? (
              <div className="flex items-center justify-center h-[220px] text-white/25 text-sm">Sin egresos este mes</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={expensesByCategory} dataKey="value" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={2}>
                      {expensesByCategory.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {expensesByCategory.map(cat => (
                    <div key={cat.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: cat.color }} />
                        <span className="text-white/60">{cat.name}</span>
                      </div>
                      <span className="text-white/80 font-mono">{fmt(cat.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Movements list ── */}
        <div className="rounded-xl border border-white/8 bg-white/3 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-white/8">
            <div>
              <p className="font-semibold text-white text-sm">Movimientos — {monthLabel}</p>
              <p className="text-white/40 text-xs mt-0.5">
                {movements.length} registros · Ingresos manuales: {fmt(totalManualIncome)} · Egresos: {fmt(totalEgresos)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={movements.length === 0}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30">
                <FileDown className="h-3.5 w-3.5 mr-1.5" />CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={movements.length === 0}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-30">
                <FileDown className="h-3.5 w-3.5 mr-1.5" />PDF
              </Button>
              <Button size="sm" onClick={() => openAdd('income')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <ArrowUpCircle className="h-3.5 w-3.5 mr-1.5" />Ingreso
              </Button>
              <Button size="sm" onClick={() => openAdd('expense')} className="bg-rose-600 hover:bg-rose-700 text-white">
                <ArrowDownCircle className="h-3.5 w-3.5 mr-1.5" />Egreso
              </Button>
            </div>
          </div>

          {loadingList ? (
            <div className="p-8 text-center text-white/30 text-sm">Cargando…</div>
          ) : movements.length === 0 ? (
            <div className="p-8 text-center text-white/25 text-sm">No hay movimientos registrados en {monthLabel}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 border-b border-white/8">
                  <tr>
                    <th className="px-4 py-3 text-left text-white/40 font-medium">Tipo</th>
                    <th className="px-4 py-3 text-left text-white/40 font-medium">Fecha</th>
                    <th className="px-4 py-3 text-left text-white/40 font-medium">Descripción</th>
                    <th className="px-4 py-3 text-left text-white/40 font-medium hidden sm:table-cell">Categoría</th>
                    <th className="px-4 py-3 text-right text-white/40 font-medium">Monto</th>
                    <th className="px-4 py-3 w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {movements.map(m => {
                    const cat = catFor(m.category)
                    const isIncome = m.kind === 'income'
                    return (
                      <tr key={`${m.kind}-${m.id}`} className="hover:bg-white/3 transition-colors">
                        <td className="px-4 py-3">
                          {isIncome
                            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><ArrowUpCircle className="h-3.5 w-3.5" />Ingreso</span>
                            : <span className="inline-flex items-center gap-1 text-xs text-rose-400"><ArrowDownCircle className="h-3.5 w-3.5" />Egreso</span>}
                        </td>
                        <td className="px-4 py-3 text-white/50 font-mono text-xs">{m.date}</td>
                        <td className="px-4 py-3 text-white/80">{m.description}</td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border"
                            style={{ color: cat.color, borderColor: cat.color + '40', background: cat.color + '15' }}>
                            {cat.name}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-semibold ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {isIncome ? '+' : '−'}{fmt(m.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {m.receipts && m.receipts.length > 0 && (
                              <button onClick={() => setViewingReceipts(m.receipts!)}
                                className="flex items-center gap-1 p-1.5 rounded text-blue-400/60 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
                                <Paperclip className="h-3.5 w-3.5" />
                                <span className="text-xs">{m.receipts.length}</span>
                              </button>
                            )}
                            <button onClick={() => openEdit(m)}
                              className="p-1.5 rounded text-white/25 hover:text-blue-400 hover:bg-blue-500/10 transition-colors" title="Editar">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => handleDelete(m)}
                              className="p-1.5 rounded text-white/25 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Eliminar">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Add / edit dialog ── */}
      <Dialog open={dialogOpen} onOpenChange={o => { if (!o) closeDialog() }}>
        <DialogContent className="bg-gray-900 border-white/10 text-white sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? (editing.kind === 'income' ? 'Editar ingreso' : 'Editar egreso')
                : (form.kind === 'income' ? 'Registrar ingreso' : 'Registrar egreso')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Kind toggle (solo al crear) */}
            {!editing && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setKind('income')}
                  className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm border transition-colors ${
                    form.kind === 'income' ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-300' : 'bg-white/5 border-white/10 text-white/50 hover:text-white/80'}`}>
                  <ArrowUpCircle className="h-4 w-4" />Ingreso
                </button>
                <button onClick={() => setKind('expense')}
                  className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm border transition-colors ${
                    form.kind === 'expense' ? 'bg-rose-600/20 border-rose-500/50 text-rose-300' : 'bg-white/5 border-white/10 text-white/50 hover:text-white/80'}`}>
                  <ArrowDownCircle className="h-4 w-4" />Egreso
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">Monto (USD)</Label>
                <Input type="number" min="0" step="0.01" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/25" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/60 text-xs">Fecha</Label>
                <Input type="date" value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-white/60 text-xs">Categoría</Label>
                <button onClick={() => setCatManagerOpen(true)}
                  className="flex items-center gap-1 text-xs text-white/40 hover:text-white/80 transition-colors" title="Administrar categorías">
                  <Settings className="h-3.5 w-3.5" /> Editar
                </button>
              </div>
              {dialogCats.length === 0 ? (
                <p className="text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  No hay categorías de {form.kind === 'income' ? 'ingreso' : 'egreso'}. Crea una con el engranaje ⚙.
                </p>
              ) : (
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/10 text-white">
                    {dialogCats.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Descripción</Label>
              <Input value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder={form.kind === 'income' ? 'ej. Venta en efectivo, abono cliente…' : 'ej. Alquiler julio, Nómina vendedores…'}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25" />
            </div>
          </div>

          {/* Existing receipts (edit mode) */}
          {existingReceipts.length > 0 && (
            <div className="space-y-2">
              <Label className="text-white/60 text-xs uppercase tracking-wide">Soportes actuales</Label>
              <div className="flex flex-wrap gap-2">
                {existingReceipts.map((r, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-white/8 rounded-lg px-2.5 py-1.5 text-xs text-white/70">
                    {r.type.startsWith('image/') ? <Image className="h-3.5 w-3.5 text-blue-400" /> : <Paperclip className="h-3.5 w-3.5 text-amber-400" />}
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="max-w-[120px] truncate hover:underline">{r.name}</a>
                    <button onClick={() => { setRemovedReceipts(prev => [...prev, r]); setExistingReceipts(prev => prev.filter((_, j) => j !== i)) }}
                      className="text-white/30 hover:text-red-400"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New attachments */}
          <div className="space-y-2">
            <Label className="text-white/60 text-xs uppercase tracking-wide">
              {existingReceipts.length > 0 ? 'Agregar más soportes' : 'Soportes (opcional)'}
            </Label>
            <div className="flex flex-wrap gap-2">
              {pendingFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-white/8 rounded-lg px-2.5 py-1.5 text-xs text-white/70">
                  {f.type.startsWith('image/') ? <Image className="h-3.5 w-3.5 text-blue-400" /> : <Paperclip className="h-3.5 w-3.5 text-amber-400" />}
                  <span className="max-w-[120px] truncate">{f.name}</span>
                  <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                    className="text-white/30 hover:text-white/70"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="flex items-center gap-1.5 cursor-pointer bg-white/5 border border-dashed border-white/20 hover:border-white/40 rounded-lg px-3 py-1.5 text-xs text-white/50 hover:text-white/80 transition-colors">
                <Upload className="h-3.5 w-3.5" />Adjuntar archivos
                <input type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files ?? [])
                    setPendingFiles(prev => {
                      const key = (f: File) => `${f.name}-${f.size}`
                      const seen = new Set(prev.map(key))
                      return [...prev, ...files.filter(f => !seen.has(key(f)))]
                    })
                    e.target.value = ''
                  }} />
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer bg-white/5 border border-dashed border-white/20 hover:border-white/40 rounded-lg px-3 py-1.5 text-xs text-white/50 hover:text-white/80 transition-colors">
                <Camera className="h-3.5 w-3.5" />Tomar foto
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files ?? [])
                    setPendingFiles(prev => [...prev, ...files])
                    e.target.value = ''
                  }} />
              </label>
            </div>
            <p className="text-xs text-white/25">
              {pendingFiles.length > 0
                ? `${pendingFiles.length} archivo(s) nuevo(s) · puedes seleccionar varios a la vez`
                : 'Selecciona varios archivos a la vez · PDF, JPG, PNG, WEBP · o toma una foto'}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              {saving && pendingFiles.length > 0 ? 'Subiendo…' : editing ? 'Guardar cambios' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Category manager ── */}
      <CategoryManager
        open={catManagerOpen}
        onClose={() => setCatManagerOpen(false)}
        categories={categories}
        onToast={toast}
      />

      {/* ── Receipt viewer ── */}
      <Dialog open={!!viewingReceipts} onOpenChange={() => setViewingReceipts(null)}>
        <DialogContent className="bg-gray-900 border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Soportes adjuntos</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {viewingReceipts?.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                className="group block rounded-lg overflow-hidden border border-white/10 hover:border-blue-500/50 transition-colors">
                {r.type.startsWith('image/') ? (
                  <img src={r.url} alt={r.name} className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 bg-white/5 gap-2">
                    <Paperclip className="h-8 w-8 text-amber-400" />
                    <span className="text-xs text-white/60 px-2 text-center truncate w-full">{r.name}</span>
                  </div>
                )}
                <div className="px-2 py-1.5 bg-white/5">
                  <p className="text-xs text-white/50 truncate">{r.name}</p>
                </div>
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
